// @flow

import * as React from 'react';
import styled, { type ReactComponentStyled } from 'styled-components';

import { colors, fontWeight, type FontWeight } from 'seedling/constants';

export type CheckedState = 'empty' | 'indeterminate' | 'checked';

export type TristateCheckboxWithLabelProps = {
  checkedState: CheckedState,
  className?: string,
  disabled: boolean,
  label: string | React.Element<any>,
  labelFontWeight: FontWeight,
  name: string,
  onChange: (event: SyntheticEvent<HTMLInputElement>) => mixed,
};

class TristateCheckboxWithLabel extends React.Component<TristateCheckboxWithLabelProps> {
  static defaultProps = {
    checkedState: 'empty',
    disabled: false,
    label: '',
    labelFontWeight: 'normal',
  };

  handleClick = (ev: SyntheticEvent<HTMLInputElement>) => {
    if (this.props.disabled) {
      return false;
    }
    return this.props.onChange(ev);
  };

  getAriaChecked = () => {
    const { checkedState } = this.props;

    switch (checkedState) {
      case 'empty':
        return 'false';
      case 'indeterminate':
        return 'mixed';
      case 'checked':
        return 'true';
    }
  };

  getBoxComponent = () => {
    const { checkedState, disabled } = this.props;

    if (disabled) {
      return BoxDisabled;
    } else {
      if (checkedState === 'empty') {
        return BoxEnabledEmpty;
      } else {
        return BoxEnabledChecked;
      }
    }
  };

  getIcon = () => {
    const { checkedState } = this.props;

    switch (checkedState) {
      case 'empty':
        return null;
      case 'indeterminate':
        return <IconDash />;
      case 'checked':
        return (
          <IconCheckmark
            xmlns="http://www.w3.org/2000/svg"
            width="12px"
            height="12px"
            viewBox="-1 -1 11 11"
          >
            <path
              fill={colors.white}
              stroke={colors.white}
              strokeWidth="0.45"
              d="M7.74102446,0.205040582 C8.04525578,-0.0843809683 8.51975872,-0.0650721456 8.80085644,0.248168045 C9.08195415,0.561408235 9.06320065,1.04996211 8.75896933,1.33938366 L3.47305862,7.79495982 C3.16967672,8.0835733 2.69675054,8.06529178 2.41524145,7.75406866 L0.20115767,5.24932692 C-0.0811395998,4.93723242 -0.064260592,4.44860609 0.238857989,4.15794947 C0.54197657,3.86729284 1.01654988,3.88467167 1.29884715,4.19676616 L3.00380162,6.13863863 L7.74102446,0.205040582 Z"
            />
          </IconCheckmark>
        );
      default:
        return null;
    }
  };

  render() {
    const { checkedState, disabled, label, name } = this.props;

    const BoxComponent = this.getBoxComponent();

    return (
      <Container disabled={disabled} onClick={this.handleClick}>
        <BoxComponent
          className="checkbox-icon"
          aria-checked={this.getAriaChecked()}
          aria-labelledby={`${name}-label`}
          checked={checkedState === 'checked'}
          disabled={disabled}
          role="checkbox"
        >
          {this.getIcon()}
        </BoxComponent>

        <Label disabled={disabled} id={`${name}-label`}>
          {label}
        </Label>
      </Container>
    );
  }
}

type StyledDiv = ReactComponentStyled<{}>;

const IconCheckmark = styled.svg``;

const IconDash = styled.div`
  background: ${colors.white};
  border-radius: 1px;
  height: 2px;
  width: 10px;
`;

export const Box = styled.div`
  align-items: center;
  border-radius: 4px;
  display: flex;
  height: 16px;
  justify-content: center;
  margin: 0;
  min-height: 16px;
  min-width: 16px;
  padding: 0;
  width: 16px;
`;
const BoxEnabledEmpty = (styled(Box)`
  border: 1px solid ${colors.mediumGrey};
  background: ${colors.white};
`: StyledDiv);
const BoxEnabledChecked = (styled(Box)`
  border: 1px solid #2c91fc;
  background: #3b99fc;

  div${IconDash} {
    filter: drop-shadow(0 1px 1px #999999);
  }
  svg${IconCheckmark} {
    filter: drop-shadow(0 1px 1px #888888);
  }
`: StyledDiv);
const BoxDisabled = (styled(Box)`
  border: 1px solid #c8c8c8;
  background: ${colors.lightGrey};
`: StyledDiv);

const Container = (styled.div`
  cursor: ${props => (!!props.disabled ? 'default' : 'pointer')};
  display: inline-flex;
  width: 100%;

  .checkbox-icon {
    margin-top: 3px;
  }

  & > div${Box} {
    margin-bottom: 0;
    margin-right: 10px;
  }
`: StyledDiv);
Container.defaultProps = {
  disabled: false,
};

export const Label = styled.span`
  cursor: ${props => (!!props.disabled ? 'default' : 'pointer')};
  font-size: 14px;
  margin-top: 2px;
  ${props => fontWeight(props.fontWeight)};
`;

export default TristateCheckboxWithLabel;
